import {LoadingButton, LoadingButtonProps} from '@mui/lab';
import {CircularProgress} from '@mui/material';
import {grey} from '@mui/material/colors';
import {SxProps, Theme} from '@mui/material/styles';
import Color from 'color';
import {forwardRef, useEffect, useState} from 'react';
import {tokens} from '../../theme/tokens';

const BUTTON_BLUE = '#15659f';

type ActionButtonVariant = 'primary' | 'outline' | 'destructive' | 'faded';
type ActionButtonSize = 'sm' | 'md' | 'lg';

export type ActionButtonProps = {
  variant?: ActionButtonVariant;
  size?: ActionButtonSize;
  cycleTime?: number;
} & Omit<LoadingButtonProps, 'variant' | 'size' | 'color'>;

const variantStyles: Record<ActionButtonVariant, SxProps<Theme>> = {
  primary: {
    backgroundColor: BUTTON_BLUE,
    color: '#fff',
    '&:hover': {
      backgroundColor: new Color(BUTTON_BLUE).darken(0.1).hex(),
    },
    '&.Mui-disabled': {
      color: grey[200],
      backgroundColor: grey[500],
    },
    '&.MuiLoadingButton-loading .MuiLoadingButton-loadingIndicator': {
      color: '#fff',
    },
  },
  outline: {
    backgroundColor: '#fff',
    color: BUTTON_BLUE,
    border: '1px solid',
    borderColor: BUTTON_BLUE,
    '&:hover': {
      backgroundColor: '#fff',
      borderColor: new Color(BUTTON_BLUE).lighten(0.3).hex(),
    },
    '&.Mui-disabled': {
      backgroundColor: '#fff',
      color: new Color(BUTTON_BLUE).lighten(0.8).hex(),
      borderColor: new Color(BUTTON_BLUE).lighten(0.8).hex(),
    },
    '&.MuiLoadingButton-loading .MuiLoadingButton-loadingIndicator': {
      color: new Color(BUTTON_BLUE).lighten(0.8).hex(),
    },
  },
  destructive: (theme: Theme) => ({
    backgroundColor: theme.palette.error.main,
    color: '#fff',
    '&:hover': {
      backgroundColor: theme.palette.error.dark,
    },
    '&.Mui-disabled': {
      color: grey[200],
      backgroundColor: grey[500],
    },
    '&.MuiLoadingButton-loading .MuiLoadingButton-loadingIndicator': {
      color: '#fff',
    },
  }),
  faded: {
    backgroundColor: '#fff',
    color: grey[500],
    border: `1px solid ${grey[400]}`,
    '&:hover': {
      backgroundColor: grey[100],
      color: grey[600],
      border: `1px solid ${grey[500]}`,
    },
    '&.Mui-disabled': {
      color: grey[600],
      backgroundColor: grey[100],
      border: `1px solid ${grey[600]}`,
    },
  },
};

const sizeStyles: Record<ActionButtonSize, SxProps<Theme>> = {
  sm: {
    padding: '4px 10px',
    fontSize: '0.8125rem',
    maxHeight: 28,
  },
  md: {},
  lg: {
    padding: '10px 22px',
    fontSize: '0.9375rem',
  },
};

const baseMuiVariant = (variant: ActionButtonVariant) =>
  variant === 'outline' || variant === 'faded' ? 'outlined' : 'contained';

const useProgressIndicator = (loading?: boolean, cycleTime?: number) => {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    setProgress(0);
    if (!loading || !cycleTime) return;
    const timer = setInterval(() => {
      setProgress(prev => prev + 5);
    }, cycleTime * 50);
    return () => clearInterval(timer);
  }, [loading, cycleTime]);

  if (!cycleTime) return undefined;
  return (
    <CircularProgress
      color="inherit"
      size={16}
      variant="determinate"
      value={progress}
      thickness={8}
    />
  );
};

export const ActionButton = forwardRef<HTMLButtonElement, ActionButtonProps>(
  (
    {variant = 'primary', size = 'md', sx: sxProp, cycleTime, ...props},
    ref,
  ) => {
    const progressIndicator = useProgressIndicator(props.loading, cycleTime);

    return (
      <LoadingButton
        ref={ref}
        variant={baseMuiVariant(variant)}
        loadingIndicator={progressIndicator}
        sx={[
          {
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: `${tokens.radius.md}px`,
            boxShadow: 'none',
            '&:hover': {boxShadow: 'none'},
          },
          variantStyles[variant],
          sizeStyles[size],
          ...(Array.isArray(sxProp) ? sxProp : sxProp ? [sxProp] : []),
        ]}
        {...props}
      />
    );
  },
);

ActionButton.displayName = 'ActionButton';
